/**
 * File: monthEndCheck.ts
 * Path: packages/config/src/accounting/monthEndCheck.ts
 * Description: The month-end Compare with Zoho check (the parallel run).
 *
 * While Zoho Books still runs beside the books, Liam compares the two at each
 * month end. Six checks, each a pure function that returns a list of
 * differences with the amounts and a plain-words explanation; a check
 * "matches" only when the list is empty:
 *
 * 1. Trial balance, account by account to the cent. Zoho's accounts are
 *    matched to the books' chart (Zoho's one "Sales" account is the books'
 *    four sales accounts, so accounts are compared in matched groups).
 * 2. Debtors, invoice by invoice with its ageing, against the books'
 *    invoice ledger, and Zoho's total against the Trade debtors control
 *    account.
 * 3. Bank: the books' bank account against the bank statement's closing
 *    balance, and Zoho's bank balance against it too.
 * 4. Profit and loss, month by month.
 * 5. Revenue per client.
 * 6. Invoice and credit-note numbers in the books: no gaps, no repeats.
 *
 * Once two month-ends match, and between them every kind of event has
 * happened at least once (a retainer cycle, a milestone invoice, a part
 * payment, a credit note, an unmatched EFT and a bank reconciliation), Zoho
 * can go read-only. A month-end's result is saved as one record
 * (accounting/{profileId}/zohoChecks/{monthEnd}) through the accounting
 * callable; this file also reads that record back.
 */

import type { Journal, LedgerAccount } from "./accountingTypes";
import type { ReconciliationRecord, BankStatementRecord } from "./bankLines";
import { BANK_ACCOUNT_CODE } from "./bankLines";
import { isBooksDate, trialBalance } from "./ledger";
import { formatCents } from "./money";
import { AGED_BUCKETS } from "./reports";
import { TIDY_MOVES } from "./booksTidyData";
import { ZOHO_EXPENSE_ACCOUNTS, ZOHO_EXPENSE_SPLITS, ZOHO_KNOWN_GAPS, ZOHO_LOAN_ACCOUNTS } from "./zohoImport";
import {
  monthEndOf,
  type ZohoAccountRow,
  type ZohoPeriod,
  type ZohoProfitAndLoss,
  type ZohoReceivables,
  type ZohoSalesByCustomer,
  type ZohoTrialBalance,
} from "./zohoReports";
import { normaliseName } from "../contracts/contractImportRows";
import { agedDebtors } from "../invoices/agedDebtors";
import { billedNumber, type InvoiceLedger } from "../invoices/invoiceLedger";
import { parseInvoiceNumber, type SavedInvoice } from "../invoices/invoiceRecord";

/* ---------------------------------------------------------------------------
 * What a check returns
 * ------------------------------------------------------------------------ */

export const MONTH_END_CHECK_IDS = ["trialBalance", "debtors", "bank", "profitAndLoss", "revenueByClient", "invoiceNumbers"] as const;
export type MonthEndCheckId = (typeof MONTH_END_CHECK_IDS)[number];

export const MONTH_END_CHECKS: Record<MonthEndCheckId, { title: string; compares: string }> = {
  trialBalance: { title: "Trial balance", compares: "Every account's balance at the month end, to the cent." },
  debtors: { title: "Debtors and ageing", compares: "Each open invoice's balance and how late it is, and the total against Trade debtors." },
  bank: { title: "Bank", compares: "The bank account in the books and in Zoho against the statement's closing balance." },
  profitAndLoss: { title: "Profit and loss by month", compares: "Income, expenses and net profit, month by month." },
  revenueByClient: { title: "Revenue per client", compares: "Sales per client for the period of Zoho's report." },
  invoiceNumbers: { title: "Invoice numbers", compares: "INV and CN numbers in the books run without gaps or repeats." },
};

export interface CheckDifference {
  /** What differs: an account, an invoice, a month, a client. */
  item: string;
  /** In plain words: what each side holds and where to look. */
  explanation: string;
  booksCents: number | null;
  zohoCents: number | null;
  /** Books less Zoho (or less the statement), when both are known. */
  differenceCents: number | null;
  /** What the first amount is when it isn't the books ("Zoho"). */
  booksLabel?: string;
  /** What the second amount is when it isn't Zoho ("Statement"). */
  zohoLabel?: string;
}

/** Longest label a saved difference keeps. */
export const DIFFERENCE_LABEL_MAX = 40;

export type MonthEndStatus = "matches" | "differences" | "waiting";

export interface CheckResult {
  id: MonthEndCheckId;
  status: MonthEndStatus;
  differences: CheckDifference[];
  /** Things worth knowing that are not differences. */
  notes: string[];
  /** Why the check hasn't run (status "waiting"). */
  waitingFor?: string;
}

const result = (id: MonthEndCheckId, differences: CheckDifference[], notes: string[] = []): CheckResult => ({
  id,
  status: differences.length === 0 ? "matches" : "differences",
  differences,
  notes,
});

export const waitingCheck = (id: MonthEndCheckId, waitingFor: string): CheckResult => ({
  id,
  status: "waiting",
  differences: [],
  notes: [],
  waitingFor,
});

const diff = (item: string, explanation: string, booksCents: number | null, zohoCents: number | null): CheckDifference => ({
  item,
  explanation,
  booksCents,
  zohoCents,
  differenceCents: booksCents !== null && zohoCents !== null ? booksCents - zohoCents : null,
});

/** "R 1 200,00 Dr" or "R 300,00 Cr"; "nil" for nothing. */
export const drCr = (cents: number): string => (cents === 0 ? "nil" : `${formatCents(Math.abs(cents))} ${cents > 0 ? "Dr" : "Cr"}`);

const fileProblems = (issues: readonly string[], what: string): CheckDifference[] =>
  issues.map((issue) => diff(`${what} file`, issue, null, null));

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "September 2026" for "2026-09". */
export const monthTitle = (month: string): string => `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
/** "30 September 2026". */
export const longDate = (iso: string): string => `${Number(iso.slice(8, 10))} ${monthTitle(iso.slice(0, 7))}`;

const DAY_MS = 86_400_000;
const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
const bucketOf = (daysLate: number): number => (daysLate <= 0 ? 0 : daysLate <= 30 ? 1 : daysLate <= 60 ? 2 : daysLate <= 90 ? 3 : 4);

/** The month-ends Liam can check: every month end from the books' start up to today, newest first. */
export const monthEndChoices = (booksStart: string, today: string): string[] => {
  const ends: string[] = [];
  let end = monthEndOf(booksStart);
  while (end <= today) {
    ends.push(end);
    const next = new Date(Date.parse(`${end}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10);
    end = monthEndOf(next);
  }
  return ends.reverse();
};

/* ---------------------------------------------------------------------------
 * Matching Zoho's accounts to the books' chart
 * ------------------------------------------------------------------------ */

const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const SALES_CODES = ["4000", "4010", "4020", "4800"];

/**
 * Entries that are in the books on purpose and never in Zoho, so the
 * trial balance, profit and loss and sales by customer leave them out and say so.
 */
export interface KnownDifference {
  reason: string;
  /** True for the journal this describes. `codes` are the account codes of its debit and credit lines. */
  matches: (journal: Journal, codes: { debit: string[]; credit: string[] }) => boolean;
}

export const KNOWN_ZOHO_DIFFERENCES: readonly KnownDifference[] = [
  {
    reason: "The Sir Bastians website deposit of R5,000 went to Liam's private account, so Zoho never had it.",
    // Liam booked it as a contractor fee to himself (6010) or subcontractor cost (5100), against sales.
    matches: (j, c) =>
      !j.externalRef &&
      !j.reversedBy &&
      !j.reversalOf &&
      j.totalCents === 500_000 &&
      j.date <= "2026-09-30" &&
      c.debit.length === 1 &&
      ["5100", "6010"].includes(c.debit[0]) &&
      c.credit.length === 1 &&
      SALES_CODES.includes(c.credit[0]),
  },
];

export interface KnownDifferenceSplit {
  /** Every journal Zoho can have, to compare against its reports. */
  comparable: Journal[];
  /** The journals left out, with why. */
  known: { journal: Journal; reason: string }[];
}

export const splitKnownDifferences = (journals: readonly Journal[], accounts: readonly Pick<LedgerAccount, "id" | "code">[]): KnownDifferenceSplit => {
  const codeOf = new Map(accounts.map((a) => [a.id, a.code]));
  const comparable: Journal[] = [];
  const known: KnownDifferenceSplit["known"] = [];
  for (const journal of journals) {
    const codes = {
      debit: journal.lines.filter((l) => l.debitCents > 0).map((l) => codeOf.get(l.accountId) ?? "?"),
      credit: journal.lines.filter((l) => l.creditCents > 0).map((l) => codeOf.get(l.accountId) ?? "?"),
    };
    const hit = KNOWN_ZOHO_DIFFERENCES.find((k) => k.matches(journal, codes));
    if (hit) known.push({ journal, reason: hit.reason });
    else comparable.push(journal);
  }
  return { comparable, known };
};

/** The note a check shows for journals left out up to the month end. */
export const knownDifferenceNotes = (known: KnownDifferenceSplit["known"], monthEnd: string): string[] =>
  known
    .filter((k) => k.journal.date <= monthEnd)
    .map((k) => `Journal ${k.journal.number} (${formatCents(k.journal.totalCents)}, ${longDate(k.journal.date)}) is left out of this check on purpose: ${k.reason}`);

/** How a Zoho account is known when Liam picks its match by hand. */
export const zohoAccountKey = (row: Pick<ZohoAccountRow, "name" | "code">): string => `${norm(row.code)}|${norm(row.name)}`;


/** Zoho names its bank account after the company (every expense in the export is "Paid Through" it). */
const ZOHO_BANK_NAMES: readonly string[] = ["idk solutions pty ltd", "thee grey company pty ltd"];

/**
 * The books' accounts a Zoho account stands for. Liam's own choice first,
 * then the same code, then what the Zoho import did with that account, then
 * the same name. Empty when nothing fits, so the screen asks.
 */
export const matchZohoAccount = (
  row: Pick<ZohoAccountRow, "name" | "code">,
  accounts: readonly LedgerAccount[],
  chosen: Readonly<Record<string, string>> = {},
): string[] => {
  const picked = chosen[zohoAccountKey(row)];
  if (picked && accounts.some((a) => a.id === picked)) return [picked];
  const byCode = (code: string) => accounts.find((a) => a.code === code);
  const byRole = (role: LedgerAccount["role"]) => accounts.find((a) => a.role === role);
  const ids = (...found: (LedgerAccount | undefined)[]) => [...new Set(found.flatMap((a) => (a ? [a.id] : [])))];
  if (row.code && byCode(row.code)) return ids(byCode(row.code));
  const name = norm(row.name);
  if (/ shareholder loan$/.test(name)) {
    const code = ZOHO_LOAN_ACCOUNTS[name.replace(/ shareholder loan$/, "")];
    return ids(code ? byCode(code) : byRole("directorLoan"));
  }
  if (/^(accounts receivable|trade debtors|debtors|trade receivables)$/.test(name)) return ids(byRole("debtors"), byRole("clientCredit"));
  if (/^(unearned revenue|customer advances?|advances? from customers|customer credits?)$/.test(name)) return ids(byRole("clientCredit"));
  if (name === "retained earnings") return ids(byRole("retainedEarnings"));
  if (/^(sales|general income|sales income|income)$/.test(name)) return ids(...SALES_CODES.map(byCode));
  if (name === "interest income") return ids(byCode("4900"));
  if (/^(accounts payable|trade creditors)$/.test(name)) return ids(byCode("2000"));
  if (/^(share capital|capital stock|owner s equity|owners equity)$/.test(name)) return ids(byCode("3000"));
  const expense = ZOHO_EXPENSE_ACCOUNTS[name];
  if (expense) {
    // The tidy-up's account and, while they still hold anything, the ones
    // merged into it; in books not tidied yet, the ones it replaces. Plus
    // any account a share of the Zoho account moved to.
    const live = (code: string) => accounts.find((a) => a.code === code && !a.archived);
    const found = [expense, ...(ZOHO_EXPENSE_SPLITS[name] ?? [])].flatMap((code) => {
      const now = byCode(code);
      const formers = TIDY_MOVES.filter((m) => m.to === code && (!now || !m.when));
      return now ? [now, ...formers.map((m) => live(m.from))] : formers.map((m) => byCode(m.from));
    });
    if (found.some(Boolean)) return ids(...found);
  }
  if (ZOHO_BANK_NAMES.includes(name)) return ids(byCode(BANK_ACCOUNT_CODE));
  const sameName = accounts.find((a) => norm(a.name) === name);
  if (sameName) return [sameName.id];
  if (/\b(fnb|bank|cheque|current account|business account)\b/.test(name) && !/\b(fee|fees|charges|interest)\b/.test(name)) {
    return ids(byCode(BANK_ACCOUNT_CODE));
  }
  return [];
};

interface Group {
  rows: number[];
  accountIds: Set<string>;
}

/** Zoho rows and the books' accounts they stand for, joined into groups that are compared as one. */
const groupMatches = (targets: readonly string[][]): Group[] => {
  const groups: Group[] = [];
  targets.forEach((ids, row) => {
    if (ids.length === 0) return;
    const touching = groups.filter((g) => ids.some((id) => g.accountIds.has(id)));
    const merged: Group = { rows: [row], accountIds: new Set(ids) };
    for (const g of touching) {
      merged.rows.push(...g.rows);
      g.accountIds.forEach((id) => merged.accountIds.add(id));
      groups.splice(groups.indexOf(g), 1);
    }
    merged.rows.sort((a, b) => a - b);
    groups.push(merged);
  });
  return groups;
};

const accountLabel = (a: LedgerAccount) => `${a.code} ${a.name}`;
const listNames = (names: string[]) => (names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

/* ---------------------------------------------------------------------------
 * 1. Trial balance
 * ------------------------------------------------------------------------ */

export interface TrialBalanceCheck extends CheckResult {
  /** Zoho accounts nothing in the books was matched to, for Liam to choose. */
  unmatched: ZohoAccountRow[];
  /** Zoho's bank balance, when its bank account is matched to the books' bank alone. */
  zohoBankCents: number | null;
}

const periodProblem = (period: ZohoPeriod | null, monthEnd: string, what: string): CheckDifference[] =>
  period && period.to !== monthEnd
    ? [diff(`${what} date`, `Zoho's ${what.toLowerCase()} runs to ${longDate(period.to)}, not the month end. Export it again for ${longDate(monthEnd)}.`, null, null)]
    : [];

export const compareTrialBalance = (input: {
  zoho: ZohoTrialBalance;
  journals: readonly Journal[];
  accounts: readonly LedgerAccount[];
  monthEnd: string;
  yearEndMonth: number;
  chosen?: Readonly<Record<string, string>>;
}): TrialBalanceCheck => {
  const { zoho, accounts, monthEnd } = input;
  const books = trialBalance(input.journals, accounts, monthEnd, input.yearEndMonth, true);
  const balance = new Map(books.rows.map((r) => [r.account.id, r.balanceCents]));
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const targets = zoho.rows.map((row) => matchZohoAccount(row, accounts, input.chosen));
  const groups = groupMatches(targets);
  const differences: CheckDifference[] = [...fileProblems(zoho.issues, "Trial balance"), ...periodProblem(zoho.period, monthEnd, "Trial balance")];

  for (const g of groups) {
    const zohoCents = g.rows.reduce((s, i) => s + zoho.rows[i].cents, 0);
    const booksCents = [...g.accountIds].reduce((s, id) => s + (balance.get(id) ?? 0), 0);
    if (zohoCents === booksCents) continue;
    const zohoNames = g.rows.map((i) => zoho.rows[i].name);
    const bookNames = [...g.accountIds].map((id) => accountLabel(byId.get(id)!));
    differences.push(
      diff(
        listNames(zohoNames),
        `Zoho's ${listNames(zohoNames)} is ${drCr(zohoCents)}; the books' ${listNames(bookNames)} ${bookNames.length > 1 ? "are" : "is"} ${drCr(booksCents)} together. Open the account in both and compare the month's entries.`,
        booksCents,
        zohoCents,
      ),
    );
  }

  const unmatched = zoho.rows.filter((_, i) => targets[i].length === 0);
  for (const row of unmatched) {
    differences.push(
      diff(row.name, `Zoho's ${row.name} (${drCr(row.cents)}) isn't matched to an account in the books. Choose the account it belongs to below.`, null, row.cents),
    );
  }

  const covered = new Set(groups.flatMap((g) => [...g.accountIds]));
  for (const row of books.rows) {
    if (row.balanceCents === 0 || covered.has(row.account.id)) continue;
    differences.push(
      diff(
        accountLabel(row.account),
        `${accountLabel(row.account)} holds ${drCr(row.balanceCents)} in the books, and no account in Zoho's trial balance was matched to it.`,
        row.balanceCents,
        null,
      ),
    );
  }

  const bank = accounts.find((a) => a.code === BANK_ACCOUNT_CODE);
  const bankGroup = bank ? groups.find((g) => g.accountIds.has(bank.id) && g.accountIds.size === 1) : undefined;
  return {
    ...result("trialBalance", differences),
    unmatched,
    zohoBankCents: bankGroup ? bankGroup.rows.reduce((s, i) => s + zoho.rows[i].cents, 0) : null,
  };
};

/* ---------------------------------------------------------------------------
 * 2. Debtors per invoice, with ageing, and the control account
 * ------------------------------------------------------------------------ */

/** "INV-000036" from "inv-36", "INV-000036" or "INV-36"; null when it isn't an invoice number. */
const invoiceKey = (text: string): string | null => {
  const n = parseInvoiceNumber(text.trim().toUpperCase());
  return n === null ? null : `INV-${String(n).padStart(6, "0")}`;
};

/** `ledger` is the books' invoice ledger as at the month end. */
export const compareDebtors = (input: { zoho: ZohoReceivables; ledger: InvoiceLedger; monthEnd: string }): CheckResult => {
  const { zoho, ledger, monthEnd } = input;
  const differences: CheckDifference[] = [...fileProblems(zoho.issues, "Receivables"), ...periodProblem(zoho.period, monthEnd, "Receivables report")];
  const notes: string[] = [];

  // The books' own ageing must add up to the control account before anything is compared.
  const aged = agedDebtors(ledger);
  if (aged.totalCents !== ledger.controlCents) {
    differences.push(
      diff(
        "Books: ageing against Trade debtors",
        `The books' aged debtors add up to ${formatCents(aged.totalCents)} but Trade debtors holds ${formatCents(ledger.controlCents)}.`,
        aged.totalCents,
        ledger.controlCents,
      ),
    );
  }

  const zohoInvoices = new Map<string, (typeof zoho.rows)[number]>();
  const others: (typeof zoho.rows)[number][] = [];
  for (const row of zoho.rows) {
    const key = invoiceKey(row.number);
    if (key && !/credit|payment/i.test(row.type)) zohoInvoices.set(key, row);
    else if (row.balanceCents !== 0) others.push(row);
  }
  const booked = new Map(ledger.invoices.filter((i) => i.journalId !== null && i.status !== "void").map((i) => [i.number, i]));
  const numbers = new Set([...zohoInvoices.keys(), ...[...booked.values()].filter((i) => i.balanceCents !== 0).map((i) => i.number)]);

  for (const number of [...numbers].sort()) {
    const z = zohoInvoices.get(number);
    const b = booked.get(number);
    const booksCents = b?.balanceCents ?? 0;
    if (!z) {
      differences.push(diff(number, `${number} (${b!.party}) still has ${formatCents(booksCents)} open in the books but isn't on Zoho's report.`, booksCents, 0));
      continue;
    }
    if (!b) {
      const voided = ledger.invoices.some((i) => i.number === number && i.status === "void");
      differences.push(
        diff(
          number,
          `${number} (${z.customer}) has ${formatCents(z.balanceCents)} open in Zoho but ${voided ? "is void in the books" : "isn't in the books"}.`,
          voided ? 0 : null,
          z.balanceCents,
        ),
      );
      continue;
    }
    if (booksCents !== z.balanceCents) {
      differences.push(
        diff(
          number,
          `${number} (${b.party}): ${formatCents(z.balanceCents)} still due in Zoho, ${formatCents(booksCents)} in the books${booksCents === 0 ? " (paid in full)" : ""}. Check its payments and credit notes in both.`,
          booksCents,
          z.balanceCents,
        ),
      );
      continue;
    }
    const zohoLate = z.dueOn ? Math.max(0, daysBetween(z.dueOn, monthEnd)) : z.ageDays;
    if (booksCents > 0 && zohoLate !== null && bucketOf(zohoLate) !== bucketOf(b.daysLate)) {
      // Zoho has one due date for the whole invoice; the books age what is left from the due date of each payment. Same amount, different due date: explained, not an error.
      if (z.dueOn && z.dueOn !== b.dueOn) {
        notes.push(
          `${number} (${b.party}): ${formatCents(booksCents)} agrees. Zoho ages it from its one due date, ${longDate(z.dueOn)}; the books age it from the due date of the payment still to come, ${longDate(b.dueOn)}. ${AGED_BUCKETS[bucketOf(b.daysLate)].label} in the books, ${AGED_BUCKETS[bucketOf(zohoLate)].label.toLowerCase()} in Zoho.`,
        );
        continue;
      }
      differences.push(
        diff(
          number,
          `${number} (${b.party}) is ${AGED_BUCKETS[bucketOf(b.daysLate)].label.toLowerCase()} in the books but ${AGED_BUCKETS[bucketOf(zohoLate)].label.toLowerCase()} in Zoho.`,
          booksCents,
          z.balanceCents,
        ),
      );
    }
  }

  const zohoTotal = zoho.rows.reduce((s, r) => s + r.balanceCents, 0);
  if (zohoTotal !== ledger.controlCents) {
    const parts = [
      ledger.unallocatedCents !== 0 ? `${formatCents(ledger.unallocatedCents)} on Trade debtors isn't tied to an invoice` : "",
      ledger.clientCreditCents !== 0 ? `${formatCents(ledger.clientCreditCents)} of clients' money paid before invoicing sits in Client credit, which Zoho may take off its total` : "",
    ].filter(Boolean);
    differences.push(
      diff(
        "Total owed (Trade debtors control account)",
        `Zoho's report adds up to ${formatCents(zohoTotal)}; Trade debtors in the books holds ${formatCents(ledger.controlCents)}.${parts.length > 0 ? ` In the books, ${parts.join("; ")}.` : ""}`,
        ledger.controlCents,
        zohoTotal,
      ),
    );
  }
  if (others.length > 0) {
    notes.push(`Zoho's report also lists ${others.map((o) => `${o.number} (${formatCents(o.balanceCents)})`).join(", ")}; they count in its total.`);
  }
  if (!zoho.rows.some((r) => r.dueOn || r.ageDays !== null)) {
    notes.push("Zoho's report has no Age or Due Date column, so the ageing wasn't compared.");
  }
  return result("debtors", differences, notes);
};

/* ---------------------------------------------------------------------------
 * 3. Bank against the statement
 * ------------------------------------------------------------------------ */

export interface StatementBalance {
  cents: number;
  /** Where the figure came from, for the screen. */
  source: string;
}

/**
 * The bank's own closing balance at the month end, when the books already
 * have it: a bank reconciliation done to that day, or an uploaded statement
 * that ends on it. Null: Liam types it from the statement.
 */
export const statementBalanceAt = (
  monthEnd: string,
  statements: readonly Pick<BankStatementRecord, "fileName" | "to" | "closingCents">[],
  reconciliations: readonly Pick<ReconciliationRecord, "to" | "closingCents" | "active">[],
): StatementBalance | null => {
  const rec = reconciliations.find((r) => r.active && r.to === monthEnd);
  if (rec) return { cents: rec.closingCents, source: `the bank reconciliation to ${longDate(monthEnd)}` };
  const statement = statements.find((s) => s.to === monthEnd && s.closingCents !== null);
  if (statement && statement.closingCents !== null) return { cents: statement.closingCents, source: `the statement ${statement.fileName}` };
  return null;
};

export const compareBank = (input: {
  monthEnd: string;
  /** The books' bank account at the month end (debit positive). */
  booksCents: number;
  statement: StatementBalance | null;
  /** Zoho's bank balance from its trial balance; null without it. */
  zohoCents: number | null;
  /** The last day the books' bank is reconciled to; null when never. */
  reconciledTo: string | null;
}): CheckResult => {
  const { monthEnd, booksCents, statement, zohoCents, reconciledTo } = input;
  if (!statement) return waitingCheck("bank", "the closing balance on the bank statement");
  const differences: CheckDifference[] = [];
  const notes: string[] = [`The statement's closing balance (from ${statement.source}) is ${formatCents(statement.cents)}.`];
  if (booksCents !== statement.cents) {
    differences.push({
      ...diff(
        "Books against the statement",
        `The books' bank account holds ${formatCents(booksCents)}; the statement closes on ${formatCents(statement.cents)}. Look for bank lines still to sort, or an entry posted twice.`,
        booksCents,
        statement.cents,
      ),
      zohoLabel: "Statement",
    });
  }
  if (zohoCents === null) notes.push("Add Zoho's trial balance to compare Zoho's bank balance too.");
  else if (zohoCents !== statement.cents) {
    differences.push({
      ...diff(
        "Zoho against the statement",
        `Zoho's bank account holds ${formatCents(zohoCents)}; the statement closes on ${formatCents(statement.cents)}. Zoho has entries the bank doesn't, or is missing some.`,
        zohoCents,
        statement.cents,
      ),
      booksLabel: "Zoho",
      zohoLabel: "Statement",
    });
  }
  if (!reconciledTo || reconciledTo < monthEnd) {
    notes.push(
      reconciledTo
        ? `The books' bank is checked against the statement only to ${longDate(reconciledTo)}. Check it to the month end in Transactions, Check against statement.`
        : "The books' bank hasn't been checked against a statement yet. Check it to the month end in Transactions, Check against statement.",
    );
  }
  return result("bank", differences, notes);
};

/* ---------------------------------------------------------------------------
 * 4. Profit and loss, month by month
 * ------------------------------------------------------------------------ */

export const compareProfitAndLoss = (input: {
  zoho: ZohoProfitAndLoss;
  journals: readonly Journal[];
  accounts: readonly LedgerAccount[];
  monthEnd: string;
  chosen?: Readonly<Record<string, string>>;
}): CheckResult => {
  const { zoho, accounts } = input;
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const pnlIds = new Set(accounts.filter((a) => a.type === "income" || a.type === "expense").map((a) => a.id));
  const differences: CheckDifference[] = fileProblems(zoho.issues, "Profit and loss");
  const month = input.monthEnd.slice(0, 7);
  if (zoho.months.length > 0 && !zoho.months.includes(month)) {
    differences.push(diff("Profit and loss months", `Zoho's report has no column for ${monthTitle(month)}. Run it to the month end.`, null, null));
  }

  // Each month's movement on every income and expense account, debit positive.
  const moved = new Map<string, Map<string, number>>();
  for (const journal of input.journals) {
    const m = journal.date.slice(0, 7);
    if (!zoho.months.includes(m)) continue;
    const row = moved.get(m) ?? new Map<string, number>();
    for (const line of journal.lines) {
      if (!pnlIds.has(line.accountId)) continue;
      row.set(line.accountId, (row.get(line.accountId) ?? 0) + line.debitCents - line.creditCents);
    }
    moved.set(m, row);
  }

  const targets = zoho.rows.map((row) => matchZohoAccount(row, accounts, input.chosen).filter((id) => pnlIds.has(id)));
  const groups = groupMatches(targets);
  // Zoho shows income and expenses both as positive figures.
  const zohoDebit = (i: number, m: string) => (zoho.rows[i].side === "income" ? -1 : 1) * (zoho.rows[i].cents[m] ?? 0);
  const plain = (debit: number, income: boolean) => (income ? -debit : debit);

  for (const m of zoho.months) {
    const books = moved.get(m) ?? new Map<string, number>();
    const booksProfit = -[...books.values()].reduce((s, c) => s + c, 0);
    const zohoProfit =
      zoho.netProfit?.[m] ?? zoho.rows.reduce((s, r) => s + (r.side === "income" ? 1 : -1) * (r.cents[m] ?? 0), 0);
    if (booksProfit !== zohoProfit) {
      differences.push(
        diff(
          `${monthTitle(m)}: net profit`,
          `Zoho's net profit for ${monthTitle(m)} is ${formatCents(zohoProfit)}; the books' is ${formatCents(booksProfit)}. The lines below show where.`,
          booksProfit,
          zohoProfit,
        ),
      );
    }
    for (const g of groups) {
      const income = g.rows.some((i) => zoho.rows[i].side === "income");
      const zohoCents = plain(g.rows.reduce((s, i) => s + zohoDebit(i, m), 0), income);
      const booksCents = plain([...g.accountIds].reduce((s, id) => s + (books.get(id) ?? 0), 0), income);
      if (zohoCents === booksCents) continue;
      const zohoNames = listNames(g.rows.map((i) => zoho.rows[i].name));
      const bookNames = listNames([...g.accountIds].map((id) => accountLabel(byId.get(id)!)));
      differences.push(
        diff(
          `${monthTitle(m)}: ${zohoNames}`,
          `Zoho's ${zohoNames} is ${formatCents(zohoCents)} for the month; the books' ${bookNames} ${formatCents(booksCents)}.`,
          booksCents,
          zohoCents,
        ),
      );
    }
    const covered = new Set(groups.flatMap((g) => [...g.accountIds]));
    for (const [id, cents] of books) {
      if (cents === 0 || covered.has(id)) continue;
      const account = byId.get(id)!;
      const amount = plain(cents, account.type === "income");
      differences.push(
        diff(
          `${monthTitle(m)}: ${accountLabel(account)}`,
          `${accountLabel(account)} has ${formatCents(amount)} for the month in the books, and no line of Zoho's report was matched to it.`,
          amount,
          null,
        ),
      );
    }
  }
  zoho.rows.forEach((row, i) => {
    if (targets[i].length > 0) return;
    const total = zoho.months.reduce((s, m) => s + (row.cents[m] ?? 0), 0);
    differences.push(diff(row.name, `Zoho's ${row.name} (${formatCents(total)} over the months shown) isn't matched to an account in the books. Choose the account it belongs to under Trial balance.`, null, total));
  });
  return result("profitAndLoss", differences);
};

/* ---------------------------------------------------------------------------
 * 5. Revenue per client
 * ------------------------------------------------------------------------ */

export const compareRevenueByClient = (input: {
  zoho: ZohoSalesByCustomer;
  journals: readonly Journal[];
  accounts: readonly LedgerAccount[];
  monthEnd: string;
}): CheckResult => {
  const { zoho, accounts, monthEnd } = input;
  const from = zoho.period?.from ?? `${monthEnd.slice(0, 8)}01`;
  const to = zoho.period?.to ?? monthEnd;
  const income = accounts.filter((a) => a.type === "income");
  const sales = income.some((a) => a.taxLine === "Sales") ? income.filter((a) => a.taxLine === "Sales") : income;
  const salesIds = new Set(sales.map((a) => a.id));
  const differences: CheckDifference[] = fileProblems(zoho.issues, "Sales by customer");
  if (zoho.period?.to && zoho.period.to !== monthEnd) {
    differences.push(diff("Sales by customer date", `Zoho's report runs to ${longDate(zoho.period.to)}, not the month end. Export it again to ${longDate(monthEnd)}.`, null, null));
  }

  const clients = new Map<string, { name: string; books: number; zoho: number }>();
  const entry = (name: string) => {
    const key = normaliseName(name) || "no name";
    const found = clients.get(key) ?? { name: name || "No client named", books: 0, zoho: 0 };
    clients.set(key, found);
    return found;
  };
  // An undo has no customer of its own: it belongs to the customer of the entry it cancels.
  const byJournal = new Map(input.journals.map((j) => [j.id, j]));
  // Zoho's Sales by Customer lists invoices only, so a sales receipt (a sale paid on the spot, no invoice) is left out and noted.
  const receiptNotes: string[] = [];
  const isReceipt = (j: Journal) => (j.externalRef ?? byJournal.get(j.reversalOf ?? "")?.externalRef ?? "").startsWith("zoho:salesreceipt:");
  for (const journal of input.journals) {
    if (journal.date < from || journal.date > to) continue;
    const cents = journal.lines.filter((l) => salesIds.has(l.accountId)).reduce((s, l) => s + l.creditCents - l.debitCents, 0);
    const party = journal.party ?? (journal.reversalOf ? byJournal.get(journal.reversalOf)?.party : null) ?? "";
    if (cents === 0) continue;
    if (isReceipt(journal)) {
      receiptNotes.push(`Journal ${journal.number} (${formatCents(cents)}, ${party || "no client"}, ${longDate(journal.date)}) is a Zoho sales receipt. Zoho's Sales by Customer lists invoices only, so it is left out of this comparison.`);
      continue;
    }
    entry(party).books += cents;
  }
  for (const row of zoho.rows) entry(row.customer).zoho += row.cents;

  for (const c of [...clients.values()].sort((a, b) => a.name.localeCompare(b.name))) {
    if (c.books === c.zoho) continue;
    differences.push(
      diff(
        c.name,
        `${c.name}: ${formatCents(c.zoho)} of sales in Zoho, ${formatCents(c.books)} in the books from ${longDate(from)} to ${longDate(to)}. Sales the books took straight from the bank (no invoice) show here too.`,
        c.books,
        c.zoho,
      ),
    );
  }
  return result("revenueByClient", differences, [`Compared from ${longDate(from)} to ${longDate(to)}.`, ...receiptNotes]);
};

/* ---------------------------------------------------------------------------
 * 6. Invoice and credit-note numbers
 * ------------------------------------------------------------------------ */

const CREDIT_NOTE_REF = /^creditnote:CN-(\d{1,9})$/;
const pad6 = (prefix: string, n: number) => `${prefix}${String(n).padStart(6, "0")}`;

export const checkInvoiceNumbers = (input: {
  journals: readonly Journal[];
  saved?: readonly SavedInvoice[];
  monthEnd: string;
}): CheckResult => {
  const { monthEnd } = input;
  const differences: CheckDifference[] = [];
  const notes: string[] = [];
  const invoices = new Map<number, Journal[]>();
  const creditNotes = new Set<number>();
  // Numbers dated after the month end: invoices aren't always dated in number order.
  const later = new Set<number>();
  for (const j of input.journals) {
    if (j.source === "reversal") continue;
    if (j.date > monthEnd) {
      const after = billedNumber(j);
      if (after) later.add(parseInvoiceNumber(after)!);
      continue;
    }
    const billed = billedNumber(j);
    if (billed) {
      const n = parseInvoiceNumber(billed)!;
      invoices.set(n, [...(invoices.get(n) ?? []), j]);
    }
    const cn = CREDIT_NOTE_REF.exec(j.externalRef ?? "");
    if (cn) creditNotes.add(Number(cn[1]));
  }
  const numbered = new Set(invoices.keys());
  for (const s of input.saved ?? []) {
    const n = parseInvoiceNumber(s.number);
    if (n === null || s.status === "discarded") continue;
    if (s.draft.issuedOn <= monthEnd) numbered.add(n);
    else later.add(n);
  }

  for (const [n, journals] of invoices) {
    const live = journals.filter((j) => !j.reversedBy);
    if (live.length > 1) {
      differences.push(
        diff(pad6("INV-", n), `${pad6("INV-", n)} is billed by ${live.length} entries in the books (journals ${live.map((j) => j.number).join(" and ")}). One of them should be reversed.`, null, null),
      );
    }
  }
  const gaps = (prefix: string, have: Set<number>, known: readonly number[]) => {
    const top = Math.max(0, ...have);
    for (let n = 1; n <= top; n += 1) {
      if (have.has(n)) continue;
      if (prefix === "INV-" && later.has(n)) {
        notes.push(`${pad6(prefix, n)} is dated after ${longDate(monthEnd)}, so it is counted in the later month.`);
        continue;
      }
      if (known.includes(n)) notes.push(`${pad6(prefix, n)} is missing: Liam confirmed Zoho deleted it as a duplicate.`);
      else differences.push(diff(pad6(prefix, n), `${pad6(prefix, n)} is missing from the books: every number up to ${pad6(prefix, top)} should be there. Find it in Zoho, or note why it was never issued.`, null, null));
    }
    return top;
  };
  const topInvoice = gaps("INV-", numbered, ZOHO_KNOWN_GAPS);
  const topCredit = gaps("CN-", creditNotes, []);
  notes.unshift(
    `${topInvoice > 0 ? `Invoices INV-000001 to ${pad6("INV-", topInvoice)}` : "No invoices"} and ${topCredit > 0 ? `credit notes CN-000001 to ${pad6("CN-", topCredit)}` : "no credit notes"} up to ${longDate(monthEnd)}.`,
  );
  return result("invoiceNumbers", differences, notes);
};

/* ---------------------------------------------------------------------------
 * What happened in the month: the event types the parallel run must cover
 * ------------------------------------------------------------------------ */

export const MONTH_END_EVENT_IDS = ["retainerCycle", "milestoneInvoice", "partPayment", "creditNote", "unmatchedEft", "bankRec"] as const;
export type MonthEndEventId = (typeof MONTH_END_EVENT_IDS)[number];

export const MONTH_END_EVENTS: Record<MonthEndEventId, string> = {
  retainerCycle: "A retainer cycle invoiced",
  milestoneInvoice: "A milestone invoice",
  partPayment: "A part payment",
  creditNote: "A credit note",
  unmatchedEft: "An EFT that matched no invoice",
  bankRec: "A bank reconciliation",
};

/** The event types the books show for the month ending `monthEnd`. `ledger` is as at that day. */
export const monthEndEvents = (input: {
  ledger: InvoiceLedger;
  reconciliations: readonly Pick<ReconciliationRecord, "to" | "active">[];
  monthEnd: string;
}): MonthEndEventId[] => {
  const { ledger, monthEnd } = input;
  const start = `${monthEnd.slice(0, 8)}01`;
  const inMonth = (d: string) => d >= start && d <= monthEnd;
  const live = ledger.invoices.filter((i) => i.journalId !== null && i.status !== "void");
  const found: Record<MonthEndEventId, boolean> = {
    retainerCycle: live.some((i) => i.retainerId !== null && inMonth(i.issuedOn)),
    milestoneInvoice: live.some((i) => i.retainerId === null && i.contractId !== null && inMonth(i.issuedOn)),
    partPayment: live.some((i) => i.balanceCents > 0 && i.movements.some((m) => m.kind === "payment" && !m.reversal && inMonth(m.date))),
    creditNote: live.some((i) => i.movements.some((m) => m.kind === "creditNote" && !m.reversal && inMonth(m.date))),
    unmatchedEft: ledger.unallocated.some((u) => u.cents < 0 && inMonth(u.date)),
    bankRec: input.reconciliations.some((r) => r.active && inMonth(r.to)),
  };
  return MONTH_END_EVENT_IDS.filter((id) => found[id]);
};

/* ---------------------------------------------------------------------------
 * The saved record and the run's progress
 * ------------------------------------------------------------------------ */

/** accounting/{profileId}/zohoChecks/{monthEnd} */
export const ZOHO_CHECKS_COLLECTION = "zohoChecks";

export const ZOHO_CHECK_LIMITS = {
  differences: 50,
  item: 200,
  explanation: 600,
  note: 2000,
  /** Matching month-ends before Zoho can go read-only. */
  needed: 2,
} as const;

export interface SavedCheck {
  id: MonthEndCheckId;
  status: Exclude<MonthEndStatus, "waiting">;
  /** Every difference found, even when only the first ones are kept. */
  differenceCount: number;
  differences: CheckDifference[];
}

export interface ZohoCheckRecord {
  /** "2026-09-30": also the document id. */
  monthEnd: string;
  /** Every check matched. Worked out by the server from the checks. */
  matching: boolean;
  checks: SavedCheck[];
  events: MonthEndEventId[];
  /** How Liam explains the differences. */
  note: string;
  savedAt: number | null;
  savedBy: string;
}

/** The checks as they are saved: the first differences of each, shortened to fit. */
export const toSavedChecks = (results: readonly CheckResult[]): SavedCheck[] =>
  results
    .filter((r): r is CheckResult & { status: SavedCheck["status"] } => r.status !== "waiting")
    .map((r) => ({
      id: r.id,
      status: r.status,
      differenceCount: r.differences.length,
      differences: r.differences.slice(0, ZOHO_CHECK_LIMITS.differences).map((d) => ({
        ...d,
        item: d.item.slice(0, ZOHO_CHECK_LIMITS.item),
        explanation: d.explanation.slice(0, ZOHO_CHECK_LIMITS.explanation),
        ...(d.booksLabel === undefined ? {} : { booksLabel: d.booksLabel.slice(0, DIFFERENCE_LABEL_MAX) }),
        ...(d.zohoLabel === undefined ? {} : { zohoLabel: d.zohoLabel.slice(0, DIFFERENCE_LABEL_MAX) }),
      })),
    }));

type Raw = Record<string, unknown>;
const centsOrNull = (v: unknown) => (typeof v === "number" && Number.isSafeInteger(v) ? v : null);

export const toZohoCheckRecord = (id: string, data: Raw | undefined | null): ZohoCheckRecord | null => {
  const r = data ?? {};
  if (!isBooksDate(id) || !Array.isArray(r.checks)) return null;
  const checks = r.checks.flatMap((c): SavedCheck[] => {
    const x = (c ?? {}) as Raw;
    if (!MONTH_END_CHECK_IDS.includes(x.id as MonthEndCheckId) || (x.status !== "matches" && x.status !== "differences")) return [];
    const differences = Array.isArray(x.differences)
      ? x.differences.map((d) => {
          const y = (d ?? {}) as Raw;
          return {
            item: typeof y.item === "string" ? y.item : "",
            explanation: typeof y.explanation === "string" ? y.explanation : "",
            booksCents: centsOrNull(y.booksCents),
            zohoCents: centsOrNull(y.zohoCents),
            differenceCents: centsOrNull(y.differenceCents),
            ...(typeof y.booksLabel === "string" ? { booksLabel: y.booksLabel } : {}),
            ...(typeof y.zohoLabel === "string" ? { zohoLabel: y.zohoLabel } : {}),
          };
        })
      : [];
    return [
      {
        id: x.id as MonthEndCheckId,
        status: x.status,
        differenceCount: typeof x.differenceCount === "number" ? x.differenceCount : differences.length,
        differences,
      },
    ];
  });
  return {
    monthEnd: id,
    matching: r.matching === true,
    checks,
    events: Array.isArray(r.events) ? r.events.filter((e): e is MonthEndEventId => MONTH_END_EVENT_IDS.includes(e as MonthEndEventId)) : [],
    note: typeof r.note === "string" ? r.note : "",
    savedAt: typeof r.savedAt === "number" ? r.savedAt : null,
    savedBy: typeof r.savedBy === "string" ? r.savedBy : "",
  };
};

/** True when every one of the six checks ran and found nothing. */
export const allChecksMatch = (checks: readonly Pick<SavedCheck, "id" | "status" | "differenceCount">[]): boolean =>
  MONTH_END_CHECK_IDS.every((id) => checks.some((c) => c.id === id && c.status === "matches" && c.differenceCount === 0));

export interface ParallelRunProgress {
  /** Month-ends where every check matched, oldest first. */
  matching: string[];
  /** Event types seen across those month-ends. */
  covered: MonthEndEventId[];
  missing: MonthEndEventId[];
  /** Zoho can go read-only. */
  done: boolean;
  /** One line for the screen. */
  summary: string;
}

/**
 * Two matching month-ends finish the run; a third only when the two didn't
 * between them see every event type.
 */
export const parallelRunProgress = (records: readonly Pick<ZohoCheckRecord, "monthEnd" | "matching" | "events">[]): ParallelRunProgress => {
  const matched = records.filter((r) => r.matching).sort((a, b) => a.monthEnd.localeCompare(b.monthEnd));
  const seen = new Set(matched.flatMap((r) => r.events));
  const covered = MONTH_END_EVENT_IDS.filter((id) => seen.has(id));
  const missing = MONTH_END_EVENT_IDS.filter((id) => !seen.has(id));
  const needed = ZOHO_CHECK_LIMITS.needed;
  const done = matched.length >= needed && (missing.length === 0 || matched.length > needed);
  const missingWords = missing.map((id) => MONTH_END_EVENTS[id].toLowerCase()).join(", ");
  const summary = done
    ? `${matched.length} matching month-ends. Zoho can go read-only.`
    : matched.length >= needed
      ? `${matched.length} matching month-ends, but none had ${missingWords}. One more matching month-end that has them finishes the run.`
      : `${matched.length} of ${needed} matching month-ends.`;
  return { matching: matched.map((r) => r.monthEnd), covered, missing, done, summary };
};
